import * as CL from '@design-system/component-library';
import { forwardRef, useEffect, useImperativeHandle, useRef } from 'react';
import { useFormContext, useWatch } from 'react-hook-form';
import { OfferSubCategory } from '../../../../common/enums.js';
import { numberDirectoryStatusMsg, t } from '../../../../common/i18n';
import { SelectRadio } from '../../../../common/react-hook-form/components/SelectRadio.js';
import { getLocalizedString } from '../../../../common/utils/productOfferingsUtils.js';
import { DirectoryDetails } from '../../../../generated/api/directoryDetails.js';
import { useCheckoutContext } from '../../context/CheckoutContextCoCo.js';
import {
  PlanProductRuntimeCharacteristic,
  findRuntimeCharacteristic,
} from '../../utils/characteristicsFlagsUtilsCoCo.js';
import { isProductSubcategory } from '../../utils/productSubcategoryUtilsCoCo.js';
import { isDefaultCharacteristicValue } from '../../utils/runtimeCharacteristicsCoCo.js';
import { NOT_TO_DIRECTORY_VALUE, schemaToCharacteristicFlags } from '../useProductConfigSchemaCoCo.js';
import { NumberPublicityCustomDetailsCoCo } from './NumberPublicityCustomDetailsCoCo';
import type { NumberPrivacyCustomDetailsHandle } from './NumberPublicityCustomDetailsCoCo';
import type { SelectRadioItem } from '../../../../common/react-hook-form/components/SelectRadio.js';
import type { ProductSpecRuntimeCharacteristicValue } from '../../../../generated/api/productSpecRuntimeCharacteristicValue.js';
import type { NumberPublicityAddressDetailsCoCo, PlaceOrderItemUnitCoCo } from '../../placeOrderTypesCoCo.js';
import type { ProductConfigSchema } from '../useProductConfigSchemaCoCo.js';

import '../../../NumberPrivacy/NumberPrivacy.scss';
import './NumberPublicityCoCo.scss';

export type DirectoryListingPublicityPatch = Partial<
  Pick<PlaceOrderItemUnitCoCo, 'directoryListingPublicity' | 'directoryListingCustomPublicityDetails'>
>;

export interface NumberPrivacySectionProps {
  schema: ProductConfigSchema;
  basketItemId: string;
  cartItemId: string;
  cartItemInstanceId: string;
  companyName?: string;
  onNumberPublicityChange?: (cartItemId: string, selectedValue: string) => void;
  initialDirectoryListingPublicity?: string;
  initialDirectoryListingPublicityDetails?: NumberPublicityAddressDetailsCoCo;
  onDirectoryListingPublicityPatch?: (patch: DirectoryListingPublicityPatch) => void;
}

export interface NumberPrivacyHandle {
  validate: () => Promise<boolean>;
  scrollToFirstError: () => void;
  getErrorCount: () => number;
}

const isCustomOption = (value?: string): boolean =>
  typeof value === 'string' && value.toUpperCase() === DirectoryDetails.NumberDirectoryEnum.CUSTOM.toUpperCase();

const buildCustomDirectoryListingSummary = (details?: NumberPublicityAddressDetailsCoCo): string | undefined => {
  const parts = [
    details?.companyName,
    details?.recipientName,
    details?.address?.line,
    details?.address?.postalCode,
    details?.address?.postOffice,
  ].filter((value): value is string => !!value);

  return parts.length > 0 ? parts.join(' ') : undefined;
};

const toRadioItems = (values: ProductSpecRuntimeCharacteristicValue[]): SelectRadioItem[] =>
  values
    .filter(option => !!option.value)
    .map(option => ({
      label: getLocalizedString(option),
      value: option.value as string,
    }))
    .sort((a, b) => {
      const aIsCustom = isCustomOption(a.value);
      const bIsCustom = isCustomOption(b.value);
      if (aIsCustom === bIsCustom) {
        return 0;
      }
      return aIsCustom ? 1 : -1;
    });

export const NumberPublicityCoCo = forwardRef<NumberPrivacyHandle, NumberPrivacySectionProps>(
  (
    {
      schema,
      basketItemId,
      cartItemId,
      cartItemInstanceId,
      companyName,
      onNumberPublicityChange,
      initialDirectoryListingPublicity,
      initialDirectoryListingPublicityDetails,
      onDirectoryListingPublicityPatch,
    },
    ref
  ) => {
    const { selectedProductOffers } = useCheckoutContext();

    const schemaFields = schemaToCharacteristicFlags(schema).fieldsToShow;
    const shouldShowNumberPublicity = schemaFields.showNumberPublicity;

    const fieldName = `configuration.${cartItemInstanceId}.directoryListingPublicity`;

    const offer = selectedProductOffers.find(o => o.id === basketItemId);
    const characteristic = findRuntimeCharacteristic(
      offer,
      PlanProductRuntimeCharacteristic.DIRECTORY_LISTING_PUBLICITY
    );
    const isMobileBroadband = isProductSubcategory(
      offer?.productOfferingType?.subcategory,
      OfferSubCategory.MOBILE_BROADBAND
    );
    const items = toRadioItems(characteristic?.value ?? []);
    const catalogueDefaultValue = isMobileBroadband
      ? NOT_TO_DIRECTORY_VALUE
      : (characteristic?.value?.find(isDefaultCharacteristicValue)?.value ?? items[0]?.value);
    const defaultRadioValue = initialDirectoryListingPublicity ?? catalogueDefaultValue;

    const selectedValue = useWatch({ name: fieldName });
    const customDetailsRef = useRef<NumberPrivacyCustomDetailsHandle>(null);
    const { setValue } = useFormContext();

    useEffect(() => {
      if (defaultRadioValue) {
        setValue(fieldName, defaultRadioValue, { shouldDirty: false });
      }
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const forcedMobileBroadbandSyncRef = useRef(false);
    useEffect(() => {
      if (
        shouldShowNumberPublicity ||
        !isMobileBroadband ||
        !characteristic ||
        !cartItemId ||
        forcedMobileBroadbandSyncRef.current
      ) {
        return;
      }
      forcedMobileBroadbandSyncRef.current = true;
      onDirectoryListingPublicityPatch?.({ directoryListingPublicity: NOT_TO_DIRECTORY_VALUE });
      onNumberPublicityChange?.(cartItemId, NOT_TO_DIRECTORY_VALUE);
    }, [
      shouldShowNumberPublicity,
      isMobileBroadband,
      characteristic,
      cartItemId,
      onDirectoryListingPublicityPatch,
      onNumberPublicityChange,
    ]);

    const lastSyncedValueRef = useRef<string | undefined>(undefined);
    useEffect(() => {
      if (!selectedValue || !cartItemId || lastSyncedValueRef.current === selectedValue) {
        return;
      }
      lastSyncedValueRef.current = selectedValue;
      onDirectoryListingPublicityPatch?.({ directoryListingPublicity: selectedValue });
      if (!isCustomOption(selectedValue)) {
        onNumberPublicityChange?.(cartItemId, selectedValue);
      }
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [selectedValue, cartItemId]);

    const isCustomSelected = isCustomOption(selectedValue ?? defaultRadioValue);

    useImperativeHandle(
      ref,
      () => ({
        validate: async () => customDetailsRef.current?.validate() ?? true,
        scrollToFirstError: () => customDetailsRef.current?.scrollToFirstError(),
        getErrorCount: () => customDetailsRef.current?.getErrorCount() ?? 0,
      }),
      []
    );

    if (!shouldShowNumberPublicity || items.length === 0) {
      return null;
    }

    return (
      <div className="of-number-privacy of-number-publicity-coco" id="number-privacy">
        <h4 className="ea-h4">
          <span>{t.L8XT(numberDirectoryStatusMsg)}</span>
          <CL.Tooltip
            className="of-number-publicity-coco__tooltip"
            triggerElement={<CL.Icon icon="information" color="secondary-blue-600" size="m" />}
            placement="top"
            i18n_tooltip_triggerAriaLabel={t.L8XT(numberDirectoryStatusMsg)}
            i18n_tooltip_contentText={[
              t.L8XT(numberDirectoryStatusMsg),
              `${t.SCL4('Public')}: ${t.EYZM('Your number and contact details may be published in directory services.')}`,
              `${t.ZGKE('Secret')}: ${t.PW0U('Your number and contact details will not be published in directory services.')}`,
              `${t.KC80('Not listed')}: ${t.LLAF(
                'Your number will not be published in directories, but it can be obtained by asking a directory service.'
              )}`,
              `${t.IW1J('Custom')}: ${t.ODWA('You can choose which directory services publish your details.')}`,
            ].join('\n\n')}
          />
        </h4>
        <SelectRadio name={fieldName} defaultValue={defaultRadioValue} items={items} />

        <NumberPublicityCustomDetailsCoCo
          ref={customDetailsRef}
          isCustomSelected={isCustomSelected}
          fieldName={fieldName}
          defaultValue={catalogueDefaultValue}
          companyName={companyName}
          initialConfirmedDetails={initialDirectoryListingPublicityDetails}
          onConfirm={details => {
            onDirectoryListingPublicityPatch?.({ directoryListingCustomPublicityDetails: details });
            const summary = buildCustomDirectoryListingSummary(details);
            if (summary && cartItemId) {
              onNumberPublicityChange?.(cartItemId, summary);
            }
          }}
        />
      </div>
    );
  }
);

NumberPublicityCoCo.displayName = 'NumberPublicityCoCo';
